import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Flex,
  SimpleGrid,
  Text,
  useToast,
  Badge,
  Icon,
  Avatar,
  VStack,
  HStack,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import EmployeeDataService from "../../services/Employee";
import { DateTime } from "luxon";
import { FiCalendar, FiClock, FiLogIn, FiLogOut } from "react-icons/fi";
import { getUserAccess } from "../../utils/authUtils";
import {
  LeaveBalanceData,
  isEligible,
  leaveTypeLabel,
  remainingText,
  sortedLeaveTypes,
  usedOfTotalText,
} from "./leaveBalanceFormat";
import { getEmployeeName } from "../../Config/Employees";
import { HrmsPageShell } from "../../components/Hrms/HrmsPageShell";

interface ProfileData {
  name: string;
  email: string;
  designation: string;
  dateOfJoining: string;
  birthday: string;
  address: string;
  reportingManager: string;
  nextAppraisalDueDate: string;
  role: string;
  isOnboarded: boolean;
}

interface TodayAttendance {
  isLoggedIn: boolean;
  loginTime: string | null;
  logoutTime: string | null;
}

interface LeaveCount {
  label: string;
  value: string; // days remaining, "No limit" or "Not eligible"
  detail: string; // "1 / 5 used"
}

export const EmployeeDashboard = () => {
  const token = localStorage.getItem("logged_in_user_token");
  const userEmail = localStorage.getItem("logged_in_email") || "";
  const userName = getEmployeeName(userEmail);
  const toast = useToast();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [isActionLoading, setIsActionLoading] = useState(false);

  const [profile, setProfile] = useState<ProfileData>({
    name: "",
    email: "",
    designation: "",
    dateOfJoining: "",
    birthday: "",
    address: "",
    reportingManager: "",
    nextAppraisalDueDate: "",
    role: "employee",
    isOnboarded: false,
  });

  const [todayAttendance, setTodayAttendance] = useState<TodayAttendance>({
    isLoggedIn: false,
    loginTime: null,
    logoutTime: null,
  });

  const [leaveCounts, setLeaveCounts] = useState<LeaveCount[]>([]);
  const [allLeaveCounts, setAllLeaveCounts] = useState<any[]>([]);
  const [needsOnboarding, setNeedsOnboarding] = useState(false);

  const [birthdaysThisMonth, setBirthdaysThisMonth] = useState<any[]>([]);
  const [anniversariesThisMonth, setAnniversariesThisMonth] = useState<any[]>([]);
  const [onLeaveToday, setOnLeaveToday] = useState<any[]>([]);
  const [spotlight, setSpotlight] = useState<any>(null);

  // New module summaries
  const [confirmationStatus, setConfirmationStatus] = useState<string>("pending");
  const [salaryStatus, setSalaryStatus] = useState<string>("pending");
  const [todayTaskCount, setTodayTaskCount] = useState<number>(0);
  const [activeTargetCount, setActiveTargetCount] = useState<number>(0);

  const { isAdmin } = getUserAccess();

  const fetchProfile = async () => {
    try {
      const res = await EmployeeDataService.getEmployeeProfileByEmail(
        token,
        userEmail,
      );
      if (res.data.status === "success" && res.data.data) {
        setProfile(res.data.data);
        const onboarded =
          res.data.data.isOnboarded === true ||
          (res.data.data.designation &&
            res.data.data.dateOfJoining &&
            res.data.data.birthday &&
            res.data.data.address);
        setNeedsOnboarding(!onboarded);
      }
    } catch (e: any) {
      if (e.response?.status === 404) {
        setNeedsOnboarding(true);
      }
      console.error("Error fetching profile:", e);
    }
  };

  const fetchTodayAttendance = async () => {
    try {
      const res = await EmployeeDataService.getTodayAttendance(
        token,
        userEmail,
      );
      if (res.data.status === "success") {
        setTodayAttendance(res.data.data);
      }
    } catch (e: any) {
      console.error("Error fetching today's attendance:", e);
    }
  };

  const fetchLeaveCounts = async () => {
    try {
      if (isAdmin) {
        const team = await EmployeeDataService.getTeamLeaveTypeBalance(token, userEmail);
        if (team.data.status === "success") {
          setAllLeaveCounts(team.data.data || []);
        }
      }

      const res = await EmployeeDataService.getLeaveTypeBalance(token, userEmail);
      const data: LeaveBalanceData | null = res.data.status === "success" ? res.data.data : null;
      if (data) {
        setLeaveCounts(
          sortedLeaveTypes(data.balances).map((code) => ({
            label: leaveTypeLabel(code),
            value: remainingText(data, code),
            // usedOfTotalText already ends in "used" for no-limit types.
            detail: isEligible(data, code)
              ? usedOfTotalText(data, code).replace(/ used$/, "") + " used"
              : "",
          })),
        );
      }
    } catch (e: any) {
      console.error("Error fetching leave balances:", e);
    }
  };

  const fetchModuleSummaries = async () => {
    const currentMonth = DateTime.now().toFormat("MM");
    const currentYear = DateTime.now().year.toString();
    const todayDate = DateTime.now().toFormat("yyyy-MM-dd");

    try {
      const [confRes, salaryRes, tasksRes, targetsRes] = await Promise.allSettled([
        EmployeeDataService.getEmployeeMonthlyConfirmations(token, userEmail, "", currentMonth, currentYear),
        EmployeeDataService.getSalary(token, userEmail, "", currentMonth, currentYear),
        EmployeeDataService.getTasks(token, userEmail, "", todayDate, "", ""),
        EmployeeDataService.getTargets(token, userEmail, ""),
      ]);

      if (confRes.status === "fulfilled" && confRes.value.data?.data) {
        const myConf = confRes.value.data.data.find(
          (c: any) => c.email === userEmail && c.month === currentMonth && c.year?.toString() === currentYear
        );
        setConfirmationStatus(myConf?.status || "pending");
      }

      if (salaryRes.status === "fulfilled" && salaryRes.value.data?.data) {
        const mySalary = salaryRes.value.data.data.find(
          (s: any) => s.email === userEmail && s.month === currentMonth && s.year?.toString() === currentYear
        );
        setSalaryStatus(mySalary?.status || "pending");
      }

      if (tasksRes.status === "fulfilled" && tasksRes.value.data?.data) {
        const todayTasks = tasksRes.value.data.data;
        const count = todayTasks.reduce((sum: number, t: any) => sum + (t.tasks?.length || 1), 0);
        setTodayTaskCount(count);
      }

      if (targetsRes.status === "fulfilled" && targetsRes.value.data?.data) {
        const active = targetsRes.value.data.data.filter(
          (t: any) => t.status !== "completed"
        );
        setActiveTargetCount(active.length);
      }
    } catch (e: any) {
      console.error("Error fetching module summaries:", e);
    }
  };

  const fetchDashboardWidgets = async () => {
    try {
      const res = await EmployeeDataService.getEmployeeDashboardWidgets(
        token,
        userEmail,
      );
      if (res.data.status === "success" && res.data.data) {
        setBirthdaysThisMonth(res.data.data.birthdaysThisMonth || []);
        setAnniversariesThisMonth(res.data.data.anniversariesThisMonth || []);
        setOnLeaveToday(res.data.data.onLeaveToday || []);
        setSpotlight(res.data.data.spotlight || null);
      }
    } catch (e: any) {
      console.error("Error fetching dashboard widgets:", e);
    }
  };

  const handleLogin = async () => {
    setIsActionLoading(true);
    try {
      const res = await EmployeeDataService.loginAttendance(
        token,
        userEmail,
        userName,
      );
      if (res.data.status === "success") {
        toast({
          position: "top",
          title: "Login Successful",
          description: "Your attendance has been marked.",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
        fetchTodayAttendance();
      }
    } catch (e: any) {
      toast({
        position: "top",
        title: "Error",
        description: e.response?.data?.message || "Failed to mark attendance.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleLogout = async () => {
    setIsActionLoading(true);
    try {
      const res = await EmployeeDataService.logoutAttendance(token, userEmail);
      if (res.data.status === "success") {
        toast({
          position: "top",
          title: "Logout Successful",
          description: "Your logout has been recorded.",
          status: "success",
          duration: 2000,
          isClosable: true,
        });
        fetchTodayAttendance();
      }
    } catch (e: any) {
      toast({
        position: "top",
        title: "Error",
        description: e.response?.data?.message || "Failed to mark logout.",
        status: "error",
        duration: 2000,
        isClosable: true,
      });
    } finally {
      setIsActionLoading(false);
    }
  };

  const formatTime = (isoString: string | null) => {
    if (!isoString) return "--:--";
    return DateTime.fromISO(isoString).toFormat("hh:mm a");
  };

  useEffect(() => {
    const loadData = async () => {
      setIsLoading(true);
      await Promise.all([
        fetchProfile(),
        fetchTodayAttendance(),
        fetchLeaveCounts(),
        fetchModuleSummaries(),
        fetchDashboardWidgets(),
      ]);
      setIsLoading(false);
    };
    loadData();
  }, []);

  const attendanceStatus = todayAttendance?.logoutTime
    ? "completed"
    : todayAttendance?.loginTime
      ? "working"
      : "not_started";

  return (
    <HrmsPageShell
      title="My Dashboard"
      sectionKey="dashboards"
      isLoading={isLoading}
    >
      <>
        {/* Onboarding Banner */}
        {needsOnboarding && (
          <Box
            bg="#308888"
            p={4}
            borderRadius="20px"
            mb={4}
            color="white"
            boxShadow="rgb(0 0 0 / 16%) 1px 1px 10px"
          >
            <Flex justify="space-between" align="center">
              <Box>
                <Text fontWeight="700" fontSize="15px" color="#fff">
                  Complete Your Profile
                </Text>
                <Text fontSize="13px" color="whiteAlpha.800">
                  Please complete your onboarding to access all features.
                </Text>
              </Box>
              <Button
                bgColor="#327171"
                color="white"
                fontWeight="600"
                fontSize="13px"
                size="sm"
                onClick={() => navigate("/hrms/profile")}
              >
                Complete Profile
              </Button>
            </Flex>
          </Box>
        )}

        {/* Welcome banner + spotlight/birthdays/anniversaries */}
        {(spotlight ||
          birthdaysThisMonth.length > 0 ||
          anniversariesThisMonth.length > 0 ||
          onLeaveToday.length > 0) && (
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={3} mb={4}>
            {spotlight && (
              <Box bg="#fdb508" p={4} borderRadius="12px" color="#212529">
                <Text fontSize="12px" fontWeight="700" textTransform="uppercase" mb={1}>
                  {spotlight.title || "Employee of the Quarter"}
                </Text>
                <Text fontSize="14px" fontWeight="600" mb={0}>
                  {spotlight.employeeEmail}
                </Text>
                {spotlight.message && (
                  <Text fontSize="12px" mt={1} mb={0}>
                    {spotlight.message}
                  </Text>
                )}
              </Box>
            )}
            {birthdaysThisMonth.length > 0 && (
              <Box bg="#e8f5e9" p={4} borderRadius="12px">
                <Text fontSize="12px" fontWeight="700" textTransform="uppercase" mb={2} color="#2e7d32">
                  Birthdays This Month
                </Text>
                {birthdaysThisMonth.map((b: any) => (
                  <Text key={b.email} fontSize="13px" mb={0}>
                    {b.name || b.email} — {DateTime.fromISO(b.date).toFormat("dd MMM")}
                  </Text>
                ))}
              </Box>
            )}
            {anniversariesThisMonth.length > 0 && (
              <Box bg="#e3f2fd" p={4} borderRadius="12px">
                <Text fontSize="12px" fontWeight="700" textTransform="uppercase" mb={2} color="#1565c0">
                  Work Anniversaries This Month
                </Text>
                {anniversariesThisMonth.map((a: any) => (
                  <Text key={a.email} fontSize="13px" mb={0}>
                    {a.name || a.email} — {a.years} year{a.years === 1 ? "" : "s"}
                  </Text>
                ))}
              </Box>
            )}
            {onLeaveToday.length > 0 && (
              <Box bg="#f3e5f5" p={4} borderRadius="12px">
                <Text fontSize="12px" fontWeight="700" textTransform="uppercase" mb={2} color="#6a1b9a">
                  On Leave Today
                </Text>
                {onLeaveToday.map((l: any) => (
                  <Text key={l.email} fontSize="13px" mb={0}>
                    {l.name || l.email}
                  </Text>
                ))}
              </Box>
            )}
          </SimpleGrid>
        )}

        {/* Row 1: Profile + Attendance */}
        <div className="row m-0">
          {/* Profile Card */}
          <div className="col-lg-6 mb-4">
            <div className="card h-100">
              <Box bg="#308888" px={4} py={3} borderTopRadius="20px">
                <Flex justify="space-between" align="center">
                  <Text color="#fff" fontWeight="700" fontSize="15px" mb={0}>
                    Profile Overview
                  </Text>
                  <Button
                        bgColor="#327171"
                    color="white"
                    fontWeight="600"
                    fontSize="13px"
                    size="sm"
                    onClick={() => navigate("/hrms/profile")}
                  >
                    View Full Profile
                  </Button>
                </Flex>
              </Box>
              <div className="card-body p-4">
                {/* Name row */}
                <Flex align="center" justify="space-between" gap={4} mb={4} wrap="wrap">
                  <HStack spacing={4} flex={1} minW="0">
                    <Avatar
                      size="lg"
                      name={profile.name || userName}
                      bg="#308888"
                      color="white"
                    />
                    <Box flex={1} minW="0">
                      <Text
                        fontSize="18px"
                        fontWeight="700"
                        color="#495057"
                        mb="0"
                      >
                        {profile.name || userName || "Employee"}
                      </Text>
                      <Text
                        fontSize="14px"
                        fontWeight="500"
                        color="#495057"
                        mb="0"
                      >
                        {profile.designation || "No designation set"}
                      </Text>
                      <Text fontSize="13px" color="#6c757d" mb="0">
                        {profile.email || userEmail}
                      </Text>
                    </Box>
                  </HStack>

                  <Badge
                    bg={isAdmin ? "#fdb508" : "#308888"}
                    color="#fff"
                    fontWeight="700"
                    fontSize="13px"
                    px={4}
                    py={1.5}
                    borderRadius="full"
                    textTransform="uppercase"
                    letterSpacing="0.6px"
                    flexShrink={0}
                  >
                    {isAdmin ? "Admin" : "Employee"}
                  </Badge>
                </Flex>

                {/* Info grid */}
                {!isAdmin && (
                <SimpleGrid columns={2} spacing={3} mb={3}>
                  <Box
                    bg="#f5f5f5"
                    p={3}
                    borderRadius="8px"
                    borderLeft="3px solid #308888"
                  >
                    <Text
                      fontSize="12px"
                      fontWeight="600"
                      color="#495057"
                      textTransform="uppercase"
                      letterSpacing="0.8px"
                    >
                      Reporting Manager
                    </Text>
                    <Text fontSize="14px" fontWeight="700" color="#000" mt={1}>
                      {profile.reportingManager || "Not assigned"}
                    </Text>
                  </Box>

                  <Box
                    bg="#f5f5f5"
                    p={3}
                    borderRadius="8px"
                    borderLeft="3px solid #fdb508"
                  >
                    <Text
                      fontSize="12px"
                      fontWeight="600"
                      color="#495057"
                      textTransform="uppercase"
                      letterSpacing="0.8px"
                    >
                      Next Appraisal
                    </Text>
                    <Text fontSize="14px" fontWeight="700" color="#000" mt={1}>
                      {profile.nextAppraisalDueDate || "Not set"}
                    </Text>
                  </Box>
                </SimpleGrid>
                )}
              </div>
            </div>
          </div>

          {/* Attendance Card - hidden on mobile */}
          <div className="col-lg-6 mb-4 d-none d-lg-block">
            <div className="card h-100">
              <Box bg="#308888" px={4} py={3} borderTopRadius="20px">
                <Flex justify="space-between" align="center">
                  <Box>
                    <HStack>
                      <Icon as={FiCalendar} color="#fff" />
                      <Text color="#fff" fontWeight="700" fontSize="15px" mb={0}>
                        Today's Attendance
                      </Text>
                    </HStack>
                    <Text color="whiteAlpha.800" fontSize="12px" fontWeight="500" mt={0.5} mb={0} ml={6}>
                      {DateTime.now().toFormat("dd MMM yyyy")}
                    </Text>
                  </Box>
                  <Button
                        bgColor="#327171"
                    color="white"
                    fontWeight="600"
                    fontSize="13px"
                    size="sm"
                    onClick={() => navigate("/hrms/attendance")}
                  >
                    View Attendance History
                  </Button>
                </Flex>
              </Box>
              <div className="card-body p-4">
                <VStack align="stretch" spacing={4}>
                  {/* Time cards */}
                  <SimpleGrid columns={2} spacing={3}>
                    <Box
                      bg={todayAttendance?.loginTime ? "#e8f5e9" : "#f5f5f5"}
                      p={4}
                      borderRadius="8px"
                      textAlign="center"
                      border="1px solid"
                      borderColor={
                        todayAttendance?.loginTime ? "#a5d6a7" : "#e2e8f0"
                      }
                    >
                      <HStack justify="center" mb={1}>
                        <Icon
                          as={FiLogIn}
                          color={
                            todayAttendance?.loginTime ? "#2e7d32" : "#495057"
                          }
                        />
                        <Text
                          fontSize="12px"
                          fontWeight="600"
                          color="#495057"
                          textTransform="uppercase"
                          letterSpacing="0.8px"
                        >
                          Login
                        </Text>
                      </HStack>
                      <Text
                        fontSize="20px"
                        fontWeight="700"
                        color={
                          todayAttendance?.loginTime ? "#2e7d32" : "#adb5bd"
                        }
                      >
                        {formatTime(todayAttendance?.loginTime)}
                      </Text>
                    </Box>

                    <Box
                      bg={todayAttendance?.logoutTime ? "#fdecea" : "#f5f5f5"}
                      p={4}
                      borderRadius="8px"
                      textAlign="center"
                      border="1px solid"
                      borderColor={
                        todayAttendance?.logoutTime ? "#f5c6cb" : "#e2e8f0"
                      }
                    >
                      <HStack justify="center" mb={1}>
                        <Icon
                          as={FiLogOut}
                          color={
                            todayAttendance?.logoutTime ? "#c62828" : "#495057"
                          }
                        />
                        <Text
                          fontSize="12px"
                          fontWeight="600"
                          color="#495057"
                          textTransform="uppercase"
                          letterSpacing="0.8px"
                        >
                          Logout
                        </Text>
                      </HStack>
                      <Text
                        fontSize="20px"
                        fontWeight="700"
                        color={
                          todayAttendance?.logoutTime ? "#c62828" : "#adb5bd"
                        }
                      >
                        {formatTime(todayAttendance?.logoutTime)}
                      </Text>
                    </Box>
                  </SimpleGrid>

                  {/* Action + Status */}
                  <Flex align="center" justify="space-between">
                    <Box>
                      {!todayAttendance?.loginTime && (
                        <Button
                          leftIcon={<FiLogIn />}
                          bgColor="#327171"
                          color="white"
                          fontWeight="600"
                          fontSize="13px"
                          size="sm"
                          onClick={handleLogin}
                          isLoading={isActionLoading}
                        >
                          Mark Login
                        </Button>
                      )}
                      {todayAttendance?.loginTime &&
                        !todayAttendance?.logoutTime && (
                          <Button
                            leftIcon={<FiLogOut />}
                            bgColor="#327171"
                            color="white"
                            fontWeight="600"
                            fontSize="13px"
                            size="sm"
                            onClick={handleLogout}
                            isLoading={isActionLoading}
                          >
                            Mark Logout
                          </Button>
                        )}
                    </Box>

                    <Badge
                      bg={
                        attendanceStatus === "completed"
                          ? "#308888"
                          : attendanceStatus === "working"
                            ? "#2e7d32"
                            : "#adb5bd"
                      }
                      color="white"
                      fontWeight="600"
                      fontSize="12px"
                      px={4}
                      py={1.5}
                      borderRadius="full"
                    >
                      {attendanceStatus === "completed"
                        ? "Day Completed"
                        : attendanceStatus === "working"
                          ? "Currently Working"
                          : "Not Started"}
                    </Badge>
                  </Flex>

                </VStack>
              </div>
            </div>
          </div>
        </div>

        {/* Row 2: Leave Balance */}
        <div className="card">
          <Box bg="#308888" px={4} py={3} borderTopRadius="20px">
            <Flex justify="space-between" align="center">
              <HStack>
                <Icon as={FiClock} color="#fff" />
                <Text color="#fff" fontWeight="700" fontSize="15px">
                  Leave Balance
                </Text>
              </HStack>
              {!isAdmin && (
                <Button
                  bgColor="#327171"
                  color="white"
                  onClick={() => {
                    navigate("/hrms/leaves/apply");
                  }}
                >
                  Apply For Leave
                </Button>
              )}
            </Flex>
          </Box>
          <div className="card-body p-4">
            {isAdmin ? (
              allLeaveCounts.length > 0 ? (
                <Box overflowX="auto">
                  <table style={{ fontSize: "13px", marginBottom: 0, minWidth: "100%", borderCollapse: "collapse" }}>
                    <thead>
                      <tr style={{ backgroundColor: "#f5f5f5" }}>
                        <th style={{ position: "sticky", left: 0, zIndex: 1, backgroundColor: "#f5f5f5", fontWeight: 700, color: "#495057", padding: "8px 12px", whiteSpace: "nowrap", borderBottom: "1px solid #e2e8f0" }}>Leave Type</th>
                        {allLeaveCounts.map((userObj: LeaveBalanceData) => (
                          <th key={userObj.email} style={{ fontWeight: 600, color: "#495057", padding: "8px 12px", textAlign: "center", whiteSpace: "nowrap", fontSize: "12px", borderBottom: "1px solid #e2e8f0" }}>
                            {userObj.name}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {sortedLeaveTypes(allLeaveCounts[0]?.balances).map((type, idx) => (
                        <tr key={type} style={{ backgroundColor: idx % 2 === 0 ? "#fff" : "#f9fafa" }}>
                          <td style={{ position: "sticky", left: 0, zIndex: 1, backgroundColor: idx % 2 === 0 ? "#fff" : "#f9fafa", fontWeight: 600, color: "#495057", padding: "8px 12px", whiteSpace: "nowrap", textTransform: "uppercase", fontSize: "11px", letterSpacing: "0.5px", borderBottom: "1px solid #eef2f2" }}>
                            {leaveTypeLabel(type)}
                          </td>
                          {allLeaveCounts.map((userObj: LeaveBalanceData) => (
                            <td key={userObj.email} style={{ textAlign: "center", fontWeight: 700, color: "#308888", padding: "8px 12px", whiteSpace: "nowrap", borderBottom: "1px solid #eef2f2" }}>
                              {userObj.balances[type] ? remainingText(userObj, type) : "-"}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </Box>
              ) : (
                <Text color="#495057" fontWeight="500" textAlign="center" py={4}>
                  No leave data available
                </Text>
              )
            ) : (
              <SimpleGrid columns={{ base: 2, md: 3, lg: 6 }} spacing={3}>
                {leaveCounts.length > 0 ? (
                  leaveCounts.map((leave) => (
                    <Box
                      key={leave.label}
                      p={4}
                      bg="#f5f5f5"
                      borderRadius="8px"
                      textAlign="center"
                      border="1px solid #e2e8f0"
                    >
                      <Text
                        fontSize="11px"
                        fontWeight="700"
                        color="#495057"
                        textTransform="uppercase"
                        letterSpacing="0.8px"
                        mb={1}
                      >
                        {leave.label}
                      </Text>
                      <Text
                        fontSize={/^[\d.]+$/.test(leave.value) ? "24px" : "15px"}
                        fontWeight="800"
                        color={leave.value === "Not eligible" ? "gray.400" : "#308888"}
                        mb={0}
                      >
                        {leave.value}
                      </Text>
                      {leave.detail && (
                        <Text fontSize="11px" color="gray.500" mb={0}>
                          {leave.detail}
                        </Text>
                      )}
                    </Box>
                  ))
                ) : (
                  <Text
                    color="#495057"
                    fontWeight="500"
                    gridColumn="span 6"
                    textAlign="center"
                    py={4}
                  >
                    No leave data available
                  </Text>
                )}
              </SimpleGrid>
            )}
          </div>
        </div>

        {/* Row 3: Module Summary Cards */}
        {!isAdmin && <div className="row m-0 mt-4">
          <div className="col-lg-4 mb-4">
            <Box
              bg="white"
              borderRadius="8px"
              border="1px solid #e2e8f0"
              p={4}
              cursor="pointer"
              onClick={() => navigate("/hrms/monthly-confirmation")}
              _hover={{ boxShadow: "md" }}
            >
              <Text
                fontSize="12px"
                fontWeight="600"
                color="#495057"
                textTransform="uppercase"
                letterSpacing="0.8px"
                mb={2}
              >
                Monthly Confirmation
              </Text>
              <Badge
                colorScheme={
                  confirmationStatus === "approved"
                    ? "green"
                    : confirmationStatus === "submitted"
                      ? "blue"
                      : "orange"
                }
                fontSize="13px"
                px={3}
                py={1}
                borderRadius="full"
              >
                {confirmationStatus.replace(/_/g, " ")}
              </Badge>
              <Text fontSize="12px" color="#6c757d" mt={2} mb={0}>
                {DateTime.now().toFormat("MMMM yyyy")}
              </Text>
            </Box>
          </div>

          <div className="col-lg-4 mb-4">
            <Box
              bg="white"
              borderRadius="8px"
              border="1px solid #e2e8f0"
              p={4}
              cursor="pointer"
              onClick={() => navigate("/hrms/salary")}
              _hover={{ boxShadow: "md" }}
            >
              <Text
                fontSize="12px"
                fontWeight="600"
                color="#495057"
                textTransform="uppercase"
                letterSpacing="0.8px"
                mb={2}
              >
                Salary Status
              </Text>
              <Badge
                colorScheme={
                  salaryStatus === "paid"
                    ? "green"
                    : salaryStatus === "on_hold"
                      ? "red"
                      : "orange"
                }
                fontSize="13px"
                px={3}
                py={1}
                borderRadius="full"
              >
                {salaryStatus.replace(/_/g, " ")}
              </Badge>
              <Text fontSize="12px" color="#6c757d" mt={2} mb={0}>
                {DateTime.now().toFormat("MMMM yyyy")}
              </Text>
            </Box>
          </div>

          <div className="col-lg-4 mb-4">
            <Box
              bg="white"
              borderRadius="8px"
              border="1px solid #e2e8f0"
              p={4}
              cursor="pointer"
              onClick={() => navigate("/hrms/tasks")}
              _hover={{ boxShadow: "md" }}
            >
              <Text
                fontSize="12px"
                fontWeight="600"
                color="#495057"
                textTransform="uppercase"
                letterSpacing="0.8px"
                mb={2}
              >
                Performance Summary
              </Text>
              <Flex gap={6}>
                <Box>
                  <Text fontSize="24px" fontWeight="800" color="#308888" mb={0}>
                    {todayTaskCount}
                  </Text>
                  <Text fontSize="11px" color="#6c757d" mb={0}>
                    Tasks Today
                  </Text>
                </Box>
                <Box>
                  <Text fontSize="24px" fontWeight="800" color="#fdb508" mb={0}>
                    {activeTargetCount}
                  </Text>
                  <Text fontSize="11px" color="#6c757d" mb={0}>
                    Active Targets
                  </Text>
                </Box>
              </Flex>
            </Box>
          </div>
        </div>}
      </>
    </HrmsPageShell>
  );
};
